import React, { useState, useEffect } from 'react';
import { CalendarDaysIcon, DocumentTextIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';

interface LectioDocument {
  id: string;
  title: string;
  date: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  lastModified: string;
}

const LectioDivina: React.FC = () => {
  const [documents, setDocuments] = useState<LectioDocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const data = await apiService.getLectioDivina();
      const documents = Array.isArray(data) ? data.filter(item => item !== null) as unknown as LectioDocument[] : [];
      setDocuments(documents);
    } catch (error) {
      console.error('Failed to load lectio divina documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-church-light to-white">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-church-dark mb-4">
              Lectio Divina
            </h1>
            <div className="w-24 h-1 bg-church-gold mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              Divine Reading - Spiritual documents and guides for prayer and reflection
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-blue mx-auto mb-4"></div>
                <p className="text-gray-600">Loading documents...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <BookOpenIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Documents Available</h3>
                <p className="text-gray-600">
                  Check back soon for spiritual reading materials and prayer guides.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold text-church-dark mb-4">
                    Available Documents
                  </h2>
                  <p className="text-gray-600">
                    Click on any document below to view or download.
                  </p>
                </div>

                <div className="grid gap-6">
                  {documents.map((document) => (
                    <div key={document.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-3">
                            <BookOpenIcon className="h-6 w-6 text-church-blue mr-3" />
                            <h3 className="text-xl font-semibold text-church-dark">
                              {document.title}
                            </h3>
                          </div>
                          
                          <div className="flex items-center text-gray-600 mb-4 space-x-6">
                            <div className="flex items-center">
                              <CalendarDaysIcon className="h-4 w-4 mr-2" />
                              <span className="text-sm">
                                {formatDate(document.date)}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-4 w-4 mr-2" />
                              <span className="text-sm">
                                {formatFileSize(document.fileSize)}
                              </span>
                            </div>
                          </div>

                          <p className="text-gray-700 text-sm mb-4">
                            Filename: {document.fileName}
                          </p>
                        </div>

                        <div className="ml-6 flex flex-col space-y-2">
                          <a
                            href={document.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-church-blue hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 text-center"
                          >
                            View Document
                          </a>
                          <a
                            href={document.fileUrl}
                            download={document.fileName}
                            className="bg-church-gold hover:bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 text-center"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-church-light rounded-lg">
                  <h3 className="text-lg font-semibold text-church-dark mb-3">
                    About Lectio Divina
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Lectio Divina, meaning "Divine Reading," is an ancient Christian practice of 
                    scriptural reading, meditation, and prayer. It involves four steps: reading 
                    (lectio), meditation (meditatio), prayer (oratio), and contemplation (contemplatio).
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Our parish offers Lectio Divina sessions on the first Friday of each month 
                    after the 10:00 AM Mass. These documents provide guidance and materials for 
                    both group sessions and personal prayer.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    For more information about our Lectio Divina sessions, please contact the 
                    parish office at{' '}
                    <a href="mailto:allsaintsthirsk@rcdmidd.org.uk" className="text-church-blue hover:underline">
                      allsaintsthirsk@rcdmidd.org.uk
                    </a>{' '}
                    or call us at{' '}
                    <a href="tel:01845527011" className="text-church-blue hover:underline">
                      01845 527011
                    </a>.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LectioDivina;